/**
 * Language switcher + full-page auto-translator.
 *
 * How it works:
 *   1. Component subscribes to LanguageContext.
 *   2. When user picks a target language (e.g. "es"), we walk the DOM
 *      collecting text nodes and translatable attributes, batch them to
 *      the backend `/api/translate` endpoint, then swap in the translations
 *      in place.
 *   3. A MutationObserver keeps translating new content as it appears
 *      (route changes, modals, etc.).
 *   4. Original strings are stashed on each node so we can restore them
 *      when the user switches back to English — no page reload needed.
 *   5. Server-side MongoDB cache ensures each unique string is translated
 *      only once ever; client-side localStorage cache avoids re-fetching.
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'cm-peppers-lang';
const CACHE_KEY_PREFIX = 'cm-peppers-tx-cache-';

const LanguageContext = createContext({
  lang: 'en',
  setLang: () => {},
  translating: false,
});

// Skip translating text inside these elements (or their descendants)
const SKIP_TAGS = new Set([
  'SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE',
  'INPUT', 'TEXTAREA', 'SELECT', 'OPTION',
]);

// Skip if the node is marked with data-no-translate="true"
const isSkipped = (node) => {
  let el = node.nodeType === 1 ? node : node.parentElement;
  while (el) {
    if (SKIP_TAGS.has(el.tagName)) return true;
    if (el.getAttribute && el.getAttribute('data-no-translate') === 'true') return true;
    if (el.getAttribute && el.getAttribute('translate') === 'no') return true;
    el = el.parentElement;
  }
  return false;
};

// Text that shouldn't be translated (numbers/prices only, single non-letter chars, etc.)
const isTranslatable = (text) => {
  if (!text) return false;
  const trimmed = text.trim();
  if (trimmed.length < 2) return false;
  // Skip pure numeric / price-like strings
  if (/^[$\d\s.,%×\-–—/:()·•+]+$/.test(trimmed)) return false;
  return true;
};

const collectTextNodes = (root) => {
  const nodes = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => {
      if (isSkipped(n)) return NodeFilter.FILTER_REJECT;
      if (!isTranslatable(n.nodeValue)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n;
  while ((n = walker.nextNode())) nodes.push(n);
  return nodes;
};

const collectAttrNodes = (root) => {
  // Placeholder / title / alt attributes
  const items = [];
  const els = root.querySelectorAll('[placeholder], [title], [alt], [aria-label]');
  els.forEach((el) => {
    if (isSkipped(el)) return;
    ['placeholder', 'title', 'alt', 'aria-label'].forEach((attr) => {
      const val = el.getAttribute(attr);
      if (val && isTranslatable(val)) items.push({ el, attr, original: val });
    });
  });
  return items;
};

const loadCache = (lang) => {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY_PREFIX + lang) || '{}');
  } catch {
    return {};
  }
};
const saveCache = (lang, cache) => {
  try {
    localStorage.setItem(CACHE_KEY_PREFIX + lang, JSON.stringify(cache));
  } catch {
    /* quota exceeded — silently skip */
  }
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => localStorage.getItem(STORAGE_KEY) || 'en');
  const [translating, setTranslating] = useState(false);
  const observerRef = useRef(null);
  const cacheRef = useRef({});
  const pendingTexts = useRef(new Set());
  const flushTimer = useRef(null);

  const applyTranslations = useCallback((mapping) => {
    // Walk again & swap text nodes / attrs whose leading trimmed value matches
    const textNodes = collectTextNodes(document.body);
    textNodes.forEach((n) => {
      const original = n.nodeValue;
      const trimmed = original.trim();
      const tr = mapping[trimmed];
      if (tr && tr !== trimmed) {
        // Stash original if not already
        if (!n.__orig) n.__orig = original;
        // Preserve leading/trailing whitespace
        const leading = original.match(/^\s*/)[0];
        const trailing = original.match(/\s*$/)[0];
        n.nodeValue = leading + tr + trailing;
      }
    });
    const attrs = collectAttrNodes(document.body);
    attrs.forEach(({ el, attr, original }) => {
      const trimmed = original.trim();
      const tr = mapping[trimmed];
      if (tr && tr !== trimmed) {
        if (!el.__origAttrs) el.__origAttrs = {};
        if (el.__origAttrs[attr] === undefined) el.__origAttrs[attr] = original;
        el.setAttribute(attr, tr);
      }
    });
  }, []);

  const restoreOriginals = useCallback(() => {
    // Walk document.body and restore any stashed originals
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    let n;
    while ((n = walker.nextNode())) {
      if (n.__orig !== undefined) {
        n.nodeValue = n.__orig;
        delete n.__orig;
      }
    }
    document.body.querySelectorAll('*').forEach((el) => {
      if (el.__origAttrs) {
        Object.entries(el.__origAttrs).forEach(([attr, val]) => {
          el.setAttribute(attr, val);
        });
        delete el.__origAttrs;
      }
    });
  }, []);

  const flushBatch = useCallback(async (targetLang) => {
    if (targetLang === 'en') return;
    const texts = Array.from(pendingTexts.current);
    pendingTexts.current.clear();
    if (!texts.length) return;

    // Filter out ones already cached client-side
    const cache = cacheRef.current;
    const missing = texts.filter((t) => cache[t] === undefined);
    let mapping = {};
    texts.forEach((t) => {
      if (cache[t] !== undefined) mapping[t] = cache[t];
    });

    if (missing.length) {
      setTranslating(true);
      try {
        const { data } = await axios.post(`${API}/translate`, {
          texts: missing,
          target_lang: targetLang,
        });
        Object.assign(mapping, data.translations);
        Object.assign(cache, data.translations);
        saveCache(targetLang, cache);
      } catch (e) {
        // Silent fail — leave originals
      } finally {
        setTranslating(false);
      }
    }

    applyTranslations(mapping);
  }, [applyTranslations]);

  const scheduleFlush = useCallback((targetLang) => {
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(() => flushBatch(targetLang), 250);
  }, [flushBatch]);

  const queueVisibleText = useCallback((targetLang) => {
    if (targetLang === 'en') return;
    const texts = collectTextNodes(document.body).map((n) => n.nodeValue.trim());
    const attrs = collectAttrNodes(document.body).map((a) => a.original.trim());
    [...texts, ...attrs].forEach((t) => {
      if (t) pendingTexts.current.add(t);
    });
    scheduleFlush(targetLang);
  }, [scheduleFlush]);

  // Change language
  const setLang = useCallback((newLang) => {
    localStorage.setItem(STORAGE_KEY, newLang);
    setLangState(newLang);
  }, []);

  // Effect: react to language changes
  useEffect(() => {
    // Tear down any existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (lang === 'en') {
      restoreOriginals();
      return;
    }

    // Load cache from localStorage
    cacheRef.current = loadCache(lang);
    document.documentElement.lang = lang;

    // Initial translation pass
    queueVisibleText(lang);

    // Watch for future mutations (route change, modals, etc.)
    const obs = new MutationObserver((mutations) => {
      let dirty = false;
      mutations.forEach((m) => {
        m.addedNodes.forEach((node) => {
          if (node.nodeType === 3 || node.nodeType === 1) {
            const textNodes =
              node.nodeType === 3
                ? isTranslatable(node.nodeValue) && !isSkipped(node) ? [node] : []
                : collectTextNodes(node);
            const attrs = node.nodeType === 1 ? collectAttrNodes(node) : [];
            textNodes.forEach((n) => {
              const t = n.nodeValue.trim();
              if (t) {
                pendingTexts.current.add(t);
                dirty = true;
              }
            });
            attrs.forEach((a) => {
              const t = a.original.trim();
              if (t) {
                pendingTexts.current.add(t);
                dirty = true;
              }
            });
          }
        });
      });
      if (dirty) scheduleFlush(lang);
    });
    obs.observe(document.body, { childList: true, subtree: true, characterData: true });
    observerRef.current = obs;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, translating }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);

export default LanguageContext;
